import React, { useState } from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * DeleteSpeakerModal Component
 * 
 * Modal for deleting a speaker and merging their blocks to another speaker
 */
const DeleteSpeakerModal = ({ isOpen, onClose, onDelete, speakerName, availableSpeakers = [] }) => {
  const [mergeToSpeaker, setMergeToSpeaker] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Filter out the current speaker from available options
  const otherSpeakers = availableSpeakers.filter(s => s.name !== speakerName);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!mergeToSpeaker) {
      setError('Please select a speaker to merge into');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await onDelete(speakerName, mergeToSpeaker);
      setMergeToSpeaker('');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to delete speaker');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMergeToSpeaker('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
      style={{ zIndex: 100000 }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            Delete Speaker
          </h3>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">
              <strong>Warning:</strong> You are about to delete speaker <strong>"{speakerName}"</strong>.
              All blocks assigned to this speaker will be reassigned to another speaker.
            </p>
          </div>

          <div>
            <label htmlFor="speaker-to-delete" className="block text-sm font-medium text-gray-700 mb-1">
              Speaker to Delete
            </label>
            <input
              id="speaker-to-delete"
              type="text"
              value={speakerName}
              disabled
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-600 font-medium"
            />
          </div>

          <div>
            <label htmlFor="merge-to-speaker" className="block text-sm font-medium text-gray-700 mb-1">
              Merge Blocks To
            </label>
            {otherSpeakers.length === 0 ? (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  No other speakers available. At least one other speaker is required to perform this action.
                </p>
              </div>
            ) : (
              <select
                id="merge-to-speaker"
                value={mergeToSpeaker}
                onChange={(e) => {
                  setMergeToSpeaker(e.target.value);
                  setError('');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
                required
              >
                <option value="">Select a speaker...</option>
                {otherSpeakers.map((speaker) => (
                  <option key={speaker.name} value={speaker.name}>
                    {speaker.name} ({speaker.count} blocks)
                  </option>
                ))}
              </select>
            )}
            {error && (
              <p className="mt-1 text-sm text-red-600">{error}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || otherSpeakers.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Deleting...' : 'Delete & Merge'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeleteSpeakerModal;

